import { useEffect, useState } from "react";
import {
  PrivacyCenterIntegrationsDto,
  PrivacyCenterIntegrationVendorDto,
} from "../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { usePrimaryOrganization } from "./useOrganization";
import { usePrivacyCenter } from "./usePrivacyCenter";
import { type AuthenticatedConnectUser, paragon } from "@useparagon/connect";
import QueryString from "qs";

// Paragon seems to choke on non-ASCII characters. Try to roughly map things to ASCII for the org name.
const toAscii = (str) => {
  return (
    str
      .normalize("NFD") // Normalize to decompose characters
      .replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks (accents, etc.)
      // eslint-disable-next-line no-control-regex
      .replace(/[^\x00-\x7F]/g, "")
  ); // Remove non-ASCII characters
};

export const useParagon = (settings?: PrivacyCenterIntegrationsDto) => {
  const [organization, orgRequest] = usePrimaryOrganization();
  const [privacyCenter, privacyCenterRequest] = usePrivacyCenter(organization.id);

  const paragonUserInfo = paragon.getUser();

  const [isAuthorized, setIsAuthorized] = useState(false);

  const [isShopifyConnected, setIsShopifyConnected] = useState(false);
  const [isShopifyLoading, setIsShopifyLoading] = useState(false);

  const paragonUserId = (paragonUserInfo as AuthenticatedConnectUser)?.userId;
  // It's possible for an e.g., admin user to switch orgs. The cookie from `connect.paragon.com` will still have the old
  // org's credentials, so check that the user (org) ID still matches the privacy center before we say we're authorized.
  // Otherwise, re-authenticate with Paragon to get the correct user ID.
  useEffect(() => {
    setIsAuthorized(paragonUserInfo?.authenticated && paragonUserInfo.userId === privacyCenter?.id);
    if ((paragonUserInfo as AuthenticatedConnectUser).integrations?.shopify?.enabled) {
      setIsShopifyConnected(true);
    }
  }, [paragonUserInfo, paragonUserId, privacyCenter?.id]);

  useEffect(() => {
    if (!settings?.featureEnabled) {
      return;
    }

    if (isAuthorized) {
      // Already connected
      return;
    }

    if (!settings?.paragonConnectedUserJWT || !settings?.paragonProjectId) {
      // No JWT or Project ID yet
      return;
    }

    paragon
      .authenticate(settings.paragonProjectId, settings.paragonConnectedUserJWT, {
        metadata: {
          Name: toAscii(organization.name),
        },
      })
      .then((_resolved) => {
        setIsAuthorized(true);
      })
      .catch(() => {
        // Paragon is *really* aggressive about caching the JWT client-side in their cookie, have to catch the exception
        // and get a new auth token.
        setIsAuthorized(false);
      });
  }, [settings, isAuthorized, organization]);

  return {
    requests: [orgRequest, privacyCenterRequest],
    isAuthorized,
    isShopifyConnected,
    isShopifyLoading,
    onConnectOrEditVendor: (vendor: PrivacyCenterIntegrationVendorDto) =>
      new Promise<void>((resolve, reject) => {
        paragon.connect(vendor.vendorAutomationName, {
          onUninstall: () => {
            resolve();
          },
          onSuccess: () => {
            resolve();
          },
          onError: reject,
        });
      }),
    onReconnectVendor: async (vendor: PrivacyCenterIntegrationVendorDto) => {
      // Paragon throws "Integration X is not installed" if we try to uninstall something it doesn't currently
      // consider installed. Our backend can flag a vendor as disconnected (e.g. the credential went INVALID and was
      // later removed in Paragon's UI) while Paragon's user state shows no installation, so check before uninstalling
      // and swallow the not-installed error if our `enabled` snapshot was stale.
      const user = paragon.getUser() as AuthenticatedConnectUser;
      const isInstalled =
        user?.authenticated && user.integrations?.[vendor.vendorAutomationName]?.enabled === true;
      if (isInstalled) {
        try {
          await paragon.uninstallIntegration(vendor.vendorAutomationName);
        } catch (e) {
          console.warn(
            `Paragon uninstallIntegration for ${vendor.vendorAutomationName} failed; falling through to connect`,
            e,
          );
        }
      }
      await new Promise<void>((resolve, reject) => {
        paragon.connect(vendor.vendorAutomationName, {
          onUninstall: () => resolve(),
          onSuccess: () => resolve(),
          onError: reject,
        });
      });
    },
    onDisconnectVendor: async (vendor: PrivacyCenterIntegrationVendorDto) => {
      // POL-7347: paragon.uninstallIntegration() guards on isAlreadyInstalled, which returns false for any non-VALID
      // credential — i.e. exactly the state this Disconnect button exists to handle. Try the SDK first for the rare
      // case where it works, then fall back to deleting the credential row directly via Paragon's REST API. That
      // endpoint accepts the same per-user JWT we use for paragon.authenticate (which the SDK exposes nowhere — its
      // getUser() filters out the `token` field — but our backend hands it to us as paragonConnectedUserJWT).
      try {
        await paragon.uninstallIntegration(vendor.vendorAutomationName);
        return;
      } catch (sdkErr) {
        const user = paragon.getUser();
        const integration = user?.authenticated
          ? (user as AuthenticatedConnectUser).integrations?.[vendor.vendorAutomationName]
          : undefined;
        // The Paragon integration UUID isn't on the SDKIntegration directly; it lives on each IConnectCredential
        // entry. Pull it from the first credential — matches what the SDK does internally via loadedIntegrations.
        const integrationId = integration?.allCredentials?.[0]?.integrationId;
        const credentialId = integration?.credentialId ?? integration?.allCredentials?.[0]?.id;
        const projectId = settings?.paragonProjectId;
        const jwt = settings?.paragonConnectedUserJWT;
        if (!integrationId || !projectId || !jwt) {
          throw sdkErr;
        }
        const response = await fetch(
          `https://api.useparagon.com/projects/${projectId}/sdk/integrations/${integrationId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${jwt}`,
              // Mirror the SDK's uninstallIntegration: pin the specific credential to remove so Paragon doesn't
              // have to guess if multiple credentials exist for one integration.
              ...(credentialId ? { "X-Paragon-Credential": credentialId } : {}),
            },
          },
        );
        if (!response.ok) {
          const body = await response.text().catch(() => "");
          throw new Error(
            `Paragon integration uninstall failed (HTTP ${response.status}): ${body || sdkErr}`,
          );
        }
      }
    },
    /**
     * @param connectionSuccessCB A callback (e.g. "getLatestData") to run after a successful Shopify connection. Passed
     *                            in instead of relying on the caller's `.then` to design around a dependency loop.
     */
    connectShopify: (connectionSuccessCB: () => void) => {
      const query = QueryString.parse(location.search.replace(/^\?/, ""));
      const { appIntegration, code, shop } = query;
      let shopName = "";
      if (shop) {
        const matches = shop.toString().match(/^([a-zA-Z0-9][a-zA-Z0-9-]*)\.myshopify\.com/);
        shopName = matches[1];
      }
      return new Promise<void>((resolve, reject) => {
        if (
          isShopifyConnected ||
          isShopifyLoading ||
          appIntegration != "shopify" ||
          !shopName ||
          !code
        ) {
          resolve();
          return;
        }
        setIsShopifyLoading(true);
        connectShopifyInner(code.toString(), shopName, (e) => {
          setIsShopifyLoading(false);
          if (e) {
            reject(e);
          } else {
            setIsShopifyConnected(true);
            connectionSuccessCB();
            resolve();
          }
        });
      });
    },
  };
};

// Shopify uses a separate process from the standard Paragon flow.
const MAX_RETRIES = 5;
const ATTEMPT_DELAY = 1000;
const connectShopifyInner = async (
  authorizationCode: string,
  shopName: string,
  cb: (e?: Error) => void,
  retries = 0,
) => {
  const integrationOptions = {};
  integrationOptions["SHOP_NAME"] = shopName;
  try {
    await paragon.completeInstall("shopify", {
      authorizationCode: authorizationCode,
      redirectUrl: `${location.protocol}//${location.host}/webhooks/oauth/shopify/complete`,
      integrationOptions,
    });
    cb();
  } catch (e) {
    if (e.message.includes("is not a valid integration type") && retries < MAX_RETRIES) {
      setTimeout(() => {
        connectShopifyInner(authorizationCode, shopName, cb, retries + 1);
      }, ATTEMPT_DELAY);
      return;
    }
    cb(e);
  }
};
